import * as ExcelJS from "exceljs";
import { addJustupBranding, setJustupMetadata } from "../../../../shared/utils/excel-branding.util";
import { CaCumuleTimeScale, computeRows } from "./ca-cumule-dialog.component";

const C = {
  headerBg: "FF1B2A4A", headerFont: "FFFFFFFF",
  groupBg: "FFE8EDF3", groupFont: "FF1B2A4A",
  border: "FFD0D5DD",
  titleColor: "FF1B2A4A",
  statGreen: "FFE6FFF5", statPurple: "FFF0E6FF", statOrange: "FFFFF0E0",
};

function headerStyle(cell: ExcelJS.Cell) {
  cell.font = { bold: true, size: 10, color: { argb: C.headerFont } };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.headerBg } };
  cell.alignment = { horizontal: "center", vertical: "middle" };
  cell.border = { bottom: { style: "medium", color: { argb: C.headerBg } } };
}
function statStyle(cell: ExcelJS.Cell, bg: string) {
  cell.font = { bold: true, size: 11 };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bg } };
  cell.alignment = { horizontal: "center", vertical: "middle" };
  const b = { style: "thin" as const, color: { argb: C.border } };
  cell.border = { top: b, bottom: b, left: b, right: b };
}

function fmtEur(n: number): string {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
}

interface ExcelData {
  tiersDenomination: string;
  facturesLignes: any[];
}

function buildSheet(wb: ExcelJS.Workbook, label: string, scale: CaCumuleTimeScale, data: ExcelData) {
  const ws = wb.addWorksheet(label, { views: [{ state: "frozen", ySplit: 5 }] });

  const COL = { periode: 1, ca: 2, cumul: 3, pct: 4 };
  [20, 16, 16, 10].forEach((w, i) => (ws.getColumn(i + 1).width = w));

  ws.mergeCells("A1:D1");
  const tc = ws.getCell("A1");
  tc.value = `CA cumulé par ${label.toLowerCase()} — ${data.tiersDenomination}`;
  tc.font = { bold: true, size: 14, color: { argb: C.titleColor } };
  ws.getRow(1).height = 26;

  const rows = computeRows(data.facturesLignes, scale);
  const totalCA = rows.at(-1)?.cumul ?? 0;
  const withCA = rows.filter((r) => r.ca > 0);
  const best = [...rows].sort((a, b) => b.ca - a.ca)[0];

  ws.mergeCells("A3:B3"); ws.mergeCells("C3:D3");
  const s1 = ws.getCell("A3");
  s1.value = `Total : ${fmtEur(totalCA)} · ${withCA.length} ${label.toLowerCase()}(s) avec CA`;
  statStyle(s1, C.statGreen);
  const s2 = ws.getCell("C3");
  s2.value = best ? `Meilleur ${label.toLowerCase()} : ${best.label} (${fmtEur(best.ca)})` : "—";
  statStyle(s2, C.statPurple);
  ws.getRow(3).height = 26;

  const headers = [label, "CA période (€)", "CA cumulé (€)", "% du total"];
  headers.forEach((h, i) => { const c = ws.getCell(5, i + 1); c.value = h; headerStyle(c); });
  ws.getRow(5).height = 22;
  ws.autoFilter = { from: { row: 5, column: 1 }, to: { row: 5, column: 4 } };

  const eurFmt = '#,##0.00\\ "€"';
  let row = 6;

  for (const r of rows) {
    const xr = ws.getRow(row);
    xr.getCell(COL.periode).value = r.label;
    xr.getCell(COL.ca).value = r.ca;
    xr.getCell(COL.cumul).value = r.cumul;
    xr.getCell(COL.pct).value = totalCA > 0 ? r.cumul / totalCA : 0;

    for (let c = 1; c <= 4; c++) {
      xr.getCell(c).font = { size: 10 };
      xr.getCell(c).border = { bottom: { style: "hair", color: { argb: C.border } } };
    }
    xr.getCell(COL.periode).font = { bold: true, size: 10, color: { argb: C.groupFont } };
    [COL.ca, COL.cumul].forEach((c) => (xr.getCell(c).numFmt = eurFmt));
    xr.getCell(COL.pct).numFmt = "0.0%";
    row++;
  }
}

export async function exportCaCumuleExcel(data: ExcelData): Promise<void> {
  const wb = new ExcelJS.Workbook();
  setJustupMetadata(wb);

  const sheets: { scale: CaCumuleTimeScale; label: string }[] = [
    { scale: "MONTH", label: "Mois" },
    { scale: "WEEK", label: "Semaine" },
    { scale: "DAY", label: "Jour" },
  ];

  for (const { scale, label } of sheets) {
    buildSheet(wb, label, scale, data);
    await addJustupBranding(wb, wb.getWorksheet(label)!, 4);
  }

  const buffer = await wb.xlsx.writeBuffer() as ArrayBuffer;
  const blob = new Blob([new Uint8Array(buffer)], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const slug = (data.tiersDenomination || "tiers").toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40);
  const filename = `ca-cumule-${slug}-${new Date().toISOString().slice(0, 10)}.xlsx`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 10000);
}
